<template>
<div class='fw-menu'>
    <component
        :is='componentType(link)'
        v-for='link in menuItems'
        :key='link.id'
        v-bind='getAttrs(link)'
        class='fw-menu__item'
        :style='{
            fontSize: itemFontSize,
            fontWeight: itemFontWeight
        }'
        :class='cssClassForMenuItem(link)'
        @click='handleClick(link)'>
        <FwIcon
            v-if='link.icon'
            class='h-mr-10'
            :icon='link.icon'
            :size='itemIconSize'
            :color='isActiveMenu(link) ? "#203f6a" : "#b1bbca"'
            :original='true'>
        </FwIcon>
        {{ link.title }}
        <div v-if='!hideUnderline && isUnderlinedType && $route && $route.path === link.to'
             class='fw-menu__underline'>
            <div></div>
        </div>
    </component>
</div>
</template>

<script lang='ts'>
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
/* eslint-disable no-unused-vars */
const UNDERLINED_TYPE = 'underlined';
const BUTTONS_TYPE = 'buttons';
const AVAILABLE_TYPES: Array<string> = [UNDERLINED_TYPE, BUTTONS_TYPE];

type FwMenuType = {
    id: number,
    to: string,
    title: string,
    icon?: string,
    active?: boolean,
    disabled?: boolean,
}

@Component({
    inheritAttrs: false,
    model: {
        event: 'update',
    },
})
export default class FwMenu extends Vue {
    @Prop({ type: Array, default: null }) readonly menuItems!: null | Array<FwMenuType>;
    @Prop({ type: String, default: 'underlined', validator: (type: string) => AVAILABLE_TYPES.includes(type) })
    readonly type!: string;
    @Prop({ type: String, default: null }) readonly itemClass!: string | null;
    @Prop({ type: String, default: '16px' }) readonly itemFontSize!: string;
    @Prop({ type: String, default: '16' }) readonly itemIconSize!: string | number;
    @Prop({ type: String, default: '500' }) readonly itemFontWeight!: string | number;
    @Prop({ type: Boolean, default: true }) readonly defaultColors!: boolean;
    @Prop({ type: Boolean, default: false }) readonly hideUnderline!: boolean;
    @Prop({ type: Number, default: null }) readonly activeId!: number;

    get isUnderlinedType(): boolean {
        return this.type === UNDERLINED_TYPE;
    }

    get typeComputed(): string {
        return `fw-menu__item--${this.type}`;
    }

    isActiveMenu(link: FwMenuType): boolean {
        if (this.activeId) {
            return link.id === this.activeId;
        }
        // @ts-ignore-next-line
        return this.$route && this.$route.path === link.to || link.active;
    }

    cssClassForMenuItem(link: FwMenuType) {
        return [
            this.itemClass,
            this.typeComputed,
            {
                'fw-menu__item--active': this.isActiveMenu(link),
                'fw-menu__item--disabled': link.disabled,
                'fw-menu__item--default-colors': this.defaultColors,
            },
        ];
    }

    componentType(link: FwMenuType): string {
        if (link.to) {
            return 'router-link';
        }
        return 'button';
    }

    getAttrs(link: FwMenuType) {
        if (link.to) {
            return {
                to: link.to,
            };
        }
        return null;
    }

    handleClick(link: FwMenuType) {
        if (!link.to && !link.disabled) {
            // @ts-ignore-next-line
            this.$emit('click', link);
        }
    }
};
</script>

<style lang='sass'>
.fw-menu
    display: flex

    &__item
        color: rgba($dark-blue, 0.3)
        padding: 10px 10px
        text-decoration: none
        transition: opacity 0.15s ease-in-out
        position: relative
        display: inline-flex
        margin: 0 10px
        outline: none

        &--buttons
            box-shadow: $base-box-shadow
            border-radius: 10px
            display: flex
            align-items: center
            justify-content: center
            font-size: 12px
            margin: 0 5px
            flex: 1 0 20%
            border: none
            cursor: pointer

            &:hover
                background-color: rgba(33,63,107,0.05)

            &.fw-menu__item--active
                background-color: rgba(33,63,107,0.1)

            &--default-colors
                background-color: #fff

        &--underlined
            &:hover
                color: rgba($dark-blue, 0.5)

        &--active
            color: $dark-blue

    &__underline
        position: absolute
        left: 0
        right: 0
        bottom: 0

        > div
            width: 33px
            height: 2px
            background-color: $dark-blue
            margin: 0 auto
</style>
